// Hooks shared by the session tabs (Agenda, Action Items).
import { useState, useRef, useEffect } from "react";
import { ADDED_ROW_HIGHLIGHT_DURATION_MS } from "../../styles/motion";
import LogManager from "../../utils/logManager";
import { parseLambdaPayload } from "../../utils/routerLambdaUtils";

/**
 * useAddedHighlight - tracks which freshly-added item should be highlighted, and clears it.
 *
 * Returns:
 *   highlightId — the id to highlight, or null
 *   flash(id)   — start the highlight for `id`
 *
 * `flash` defers the state change by one frame so the highlight class lands after the new row has
 * mounted; applying it in the same commit can drop the CSS animation's first frames (or the whole
 * animation) because the element is painted with the class already present.
 *
 * Re-flashing supersedes any in-flight highlight rather than stacking timers, so rapid adds
 * highlight the newest row only. Both the timeout and the frame request are cancelled on unmount.
 */
export const useAddedHighlight = () => {
  const [highlightId, setHighlightId] = useState(null);
  const timerRef = useRef(null);
  const frameRef = useRef(null);

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      cancelAnimationFrame(frameRef.current);
    },
    []
  );

  const flash = (id) => {
    cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => {
      setHighlightId(id);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setHighlightId(null), ADDED_ROW_HIGHLIGHT_DURATION_MS);
    });
  };

  return { highlightId, flash };
};

/**
 * useScrollDismissedTooltip - Meridian repositions its popover on scroll instead of closing, so
 * it parks at the window edge. Owning `open` disables its hover handling, hence `anchorProps`.
 */
export const useScrollDismissedTooltip = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    // Capture phase: the list scrolls, not the window, and scroll events don't bubble.
    document.addEventListener("scroll", close, true);
    return () => document.removeEventListener("scroll", close, true);
  }, [open]);

  return {
    open,
    anchorProps: {
      onMouseEnter: () => setOpen(true),
      onMouseLeave: () => setOpen(false),
    },
  };
};

/**
 * useHoverReveal - Shared hover/focus reveal for a row's action controls.
 *
 * Returns:
 *   active        — true when the element is hovered or focused
 *   bind          — spread onto the wrapper element: { onMouseEnter, onMouseLeave, onFocus, onBlur }
 *   controlsStyle — { opacity, pointerEvents } to apply to the controls container
 *
 * Used by both AgendaRowLabel and ActionItemCard so the reveal behaviour stays consistent and
 * keyboard/screen-reader accessible (focus reveals the controls, not just the pointer).
 */
export const useHoverReveal = () => {
  const [active, setActive] = useState(false);

  const bind = {
    onMouseEnter: () => setActive(true),
    onMouseLeave: () => setActive(false),
    onFocus: () => setActive(true),
    onBlur: () => setActive(false),
  };

  // The two halves of the crossfade. Callers that overlay a resting label behind the controls need
  // the inverse, so both live here rather than one being hand-rolled at the call site — that way
  // they can't drift out of step.
  const controlsStyle = {
    opacity: active ? 1 : 0,
    pointerEvents: active ? "auto" : "none",
  };

  // Never interactive: it's a resting label that yields to the controls on hover/focus.
  const restingStyle = {
    opacity: active ? 0 : 1,
    pointerEvents: "none",
  };

  return { active, bind, controlsStyle, restingStyle };
};

/**
 * useInsightSeed - Shared one-shot seed for insight lists, so a tab's count fills before the tab
 * is opened. Seeds once per Home mount, so an emptied list doesn't re-fetch and the seed bows out
 * when the WebSocket or poll gets there first.
 */
export const useInsightSeed = ({
  meetingUuid,
  insightStore,
  isSessionActive,
  isTranscriptOnly,
  isEnabled,
  fetchItems,
  upsertItems,
  errorLabel,
}) => {
  const hasSeededRef = useRef(false);

  useEffect(() => {
    if (!isEnabled) return undefined;
    if (!meetingUuid) return undefined;
    if (!isSessionActive) return undefined;
    // Transcript-only sessions also reach SUMMARIZING, but these endpoints aren't served for
    // them — posting there 403s and trips a misuse signal.
    if (isTranscriptOnly) return undefined;
    if (hasSeededRef.current) return undefined;

    let stale = false;
    const seed = async () => {
      try {
        const items = await fetchItems(meetingUuid);
        // Bail if superseded (unmount, meetingUuid/session change) so a late response can't
        // clobber fresher state.
        if (stale) return;
        if (items.length > 0) {
          upsertItems(items);
          hasSeededRef.current = true;
        }
      } catch (err) {
        if (!stale) LogManager.error(errorLabel, err);
      }
    };

    seed();
    return () => {
      stale = true;
    };
    // Not keyed on the store's list length: this is a seed-once effect guarded by hasSeededRef,
    // not one that should re-fire whenever the list changes.
  }, [meetingUuid, isSessionActive, isTranscriptOnly, isEnabled]);
};

/**
 * useInsightPolling - Shared fetch + fallback-polling hook for meeting insight lists
 * (action items, agenda). Consolidates the near-identical logic that previously lived
 * inline in ActionItems.jsx and Agenda.jsx.
 *
 * The insight store (useInsightStore) is the single source of truth for the list. This hook
 * does NOT keep its own copy — it reads the list from `insightStore[storeKey]` and writes
 * fetch/poll results back through `insightStore[upsertKey]`, which merges by itemId (never a
 * wholesale replace). It owns only the view-state (isLoading / error) for the initial fetch.
 *
 * Behavior:
 *   - Primary mode: items arrive via WebSocket push through insightStore.
 *   - One-time initial fetch on mount to establish state.
 *   - Fallback polling every `pollIntervalMs` only while the WebSocket is NOT connected.
 *
 * Config:
 *   endpoint / pollIntervalMs / storeKey / responseKey / upsertKey — wiring.
 *   requireNonEmpty  — only upsert a response payload when it has at least one item.
 *   skipInitialFetchIfSeeded — skip the initial fetch when the store is already populated.
 *   useSince         — send `since = newest detectedAt` on poll requests.
 *   requestExtras    — extra fields merged into every request body (e.g. agenda's `operation`).
 *   mapItems         — maps the raw response array to the shape the store holds (e.g. agenda's
 *                      backend itemId/content/status → frontend id/title/state). Identity default.
 *   upsertOptions    — options forwarded to the store's upsert on fetch/poll only (e.g. agenda's
 *                      `pruneMissing`, which is safe here because the response is a full snapshot).
 *
 * @returns {{ items, isLoading, error }}
 */
export const useInsightPolling = ({
  meetingUuid,
  insightStore,
  endpoint,
  pollIntervalMs,
  storeKey,
  responseKey,
  upsertKey,
  errorLabel,
  pollErrorLabel,
  errorMessage,
  initialIsLoading = true,
  initialHasFetched = false,
  requireNonEmpty = false,
  skipInitialFetchIfSeeded = false,
  useSince = false,
  requestExtras = null,
  mapItems = null,
  upsertOptions = null,
}) => {
  const [isLoading, setIsLoading] = useState(initialIsLoading);
  const [error, setError] = useState(null);

  const pollIntervalRef = useRef(null);
  const hasFetchedRef = useRef(initialHasFetched);
  const itemsRef = useRef([]);

  // The store is the single source of truth; this hook never keeps a second copy.
  const items = insightStore?.[storeKey] ?? [];

  // Mirror the store list into a ref so the poll closure can read the current items (for the
  // `since` cursor) without needing them in its effect dependency array.
  itemsRef.current = items;

  // Merge a response payload into the store (no-op when absent / empty-when-required).
  const applyPayload = (payload) => {
    if (payload == null || (requireNonEmpty && payload.length === 0)) return;
    if (insightStore?.[upsertKey]) {
      insightStore[upsertKey](mapItems ? payload.map(mapItems) : payload, upsertOptions ?? undefined);
    }
  };

  // One-time initial fetch on mount.
  useEffect(() => {
    if (!meetingUuid || hasFetchedRef.current) return undefined;

    // If the store was already seeded (e.g. by Home.jsx), skip the fetch.
    if (skipInitialFetchIfSeeded && items.length > 0) {
      hasFetchedRef.current = true;
      setIsLoading(false);
      return undefined;
    }

    // Guard against unmount / meetingUuid change while the fetch is in flight, so a late
    // response can't setState-after-unmount (the store upsert itself is race-safe — it merges).
    let stale = false;
    const fetchInitial = async () => {
      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ meetingUuid, ...requestExtras }),
        });

        const data = await parseLambdaPayload(response, endpoint);
        if (stale) return;

        applyPayload(data[responseKey]);
        setError(null);
        hasFetchedRef.current = true;
      } catch (err) {
        LogManager.error(errorLabel, err);
        if (!stale) setError(errorMessage);
      } finally {
        if (!stale) setIsLoading(false);
      }
    };

    fetchInitial();
    return () => {
      stale = true;
    };
  }, [meetingUuid]);

  // Clear a stale initial-fetch error once any items exist or the WebSocket is
  // connected — the poll's setError(null) only runs while !wsConnected, so if the
  // initial fetch fails with the WebSocket connected and items later arrive via push
  // (or the meeting legitimately has zero items), the error would otherwise linger and
  // hide the populated list.
  const wsConnected = insightStore?.wsConnected ?? false;
  useEffect(() => {
    if (wsConnected || items.length > 0) {
      setError(null);
    }
  }, [wsConnected, items.length]);

  // Fallback polling — only active when the WebSocket is not connected.
  useEffect(() => {
    if (!meetingUuid || wsConnected) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    // Guard against (1) a poll resolving after the WebSocket connects / meetingUuid changes and
    // (2) overlapping requests when a fetch outlives pollIntervalMs.
    let stale = false;
    let inFlight = false;

    const poll = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const request = { meetingUuid, ...requestExtras };
        if (useSince) {
          // Use the newest detectedAt across items, not items[0] — don't assume the list is
          // sorted newest-first (mocks/servers may not be), so the cursor stays correct.
          const newest = itemsRef.current
            .map((i) => Date.parse(i.detectedAt))
            .filter((t) => !Number.isNaN(t))
            .reduce((max, t) => (t > max ? t : max), -Infinity);
          if (newest > -Infinity) {
            request.since = new Date(newest).toISOString();
          }
        }

        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(request),
        });

        const data = await parseLambdaPayload(response, endpoint);
        // The WebSocket connected (or meetingUuid changed) while this fetch was outstanding; its
        // result is now stale relative to WS-pushed state, so drop it.
        if (stale) return;

        applyPayload(data[responseKey]);
        // Clear any prior initial-fetch error now that a poll has succeeded.
        setError(null);
      } catch (err) {
        LogManager.error(pollErrorLabel ?? errorLabel, err);
      } finally {
        inFlight = false;
      }
    };

    pollIntervalRef.current = setInterval(poll, pollIntervalMs);

    return () => {
      stale = true;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [meetingUuid, wsConnected]);

  return { items, isLoading, error };
};
